import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DateField } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, CheckCircle2 } from "lucide-react";

export function FileUploadField({ kind, label, onUploaded, dataTestId }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/upload?kind=${kind}`, fd);
      setFile(r.data);
      onUploaded(r.data.path);
      toast.success(`${f.name} uploaded`);
    } catch (err) {
      toast.error(String(err.response?.data?.detail || "Upload failed"));
    } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      {file ? (
        <p className="text-xs text-signal-green flex items-center gap-1 mt-1" data-testid={`${dataTestId}-done`}>
          <CheckCircle2 className="w-4 h-4" /> {file.original_filename}
        </p>
      ) : (
        <label className="mt-1 inline-flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-border cursor-pointer text-sm text-muted-foreground hover:text-foreground">
          <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Choose file"}
          <input type="file" className="hidden" onChange={pick} data-testid={dataTestId} accept=".pdf,.jpg,.jpeg,.png,.webp" />
        </label>
      )}
    </div>
  );
}

const F = ({ label, value, onChange, testId, type = "text", required }) => (
  <div>
    <Label className="text-xs">{label}{required ? " *" : ""}</Label>
    <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
  </div>
);


export function WorkflowCompleteDialog({ task, onClose, onDone }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  if (!task) return null;
  const t = task.type;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setBusy(true);
    const endpoints = {
      einvoice: ["einvoice-complete", { invoice_no: f.invoice_no, invoice_date: f.invoice_date || null, irn: f.irn || null, ack_no: f.ack_no || null, ack_date: f.ack_date || null, eway_bill: f.eway_bill || null, remarks: f.remarks || null, document_path: f.document_path }],
      vehicle_checkin: ["vehicle-complete", { vehicle_number: f.vehicle_number, driver_name: f.driver_name, driver_mobile: f.driver_mobile || null, transporter: f.transporter || null, vehicle_type: f.vehicle_type || null, loading_bay: f.loading_bay || null, remarks: f.remarks || null }],
      handover: ["handover-complete", { receiver_name: f.receiver_name, receiver_mobile: f.receiver_mobile || null, quantity: parseInt(f.quantity) || 0, remarks: f.remarks || null, proof_path: f.document_path || null }],
      pod: ["pod-complete", { document_path: f.document_path, remarks: f.remarks || null }],
      customer_feedback: ["feedback-complete", { option: f.option, remarks: f.remarks || null, attachment_path: f.document_path || null }],
      customer_communication: ["comm-complete", { method: f.method, remarks: f.remarks || null }],
    };
    const [endpoint, payload] = endpoints[t];
    try {
      await api.post(`/tasks/${task.task_id}/${endpoint}`, payload);
      toast.success("Task completed");
      onClose();
      onDone?.();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed to complete");
    } finally { setBusy(false); }
  };

  const titles = {
    einvoice: "E-Invoice — Create in Tally & Upload Proof",
    vehicle_checkin: "Vehicle Check-in",
    handover: "Customer Pickup Handover",
    pod: "POD Upload & Verification",
    customer_feedback: "Customer Delivery Feedback",
    customer_communication: "Share Dispatch Documents",
  };

  return (
    <Dialog open={!!task} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-h-[90vh] overflow-y-auto" data-testid="workflow-complete-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl uppercase">{titles[t]} — {task.task_id}</DialogTitle><DialogDescription className="sr-only">Complete workflow task with required operational details</DialogDescription></DialogHeader>
        {task.bill_no && <p className="text-xs font-mono text-muted-foreground">Order <span className="text-primary">{task.bill_no}</span>{task.remarks ? ` · ${task.remarks}` : ""}</p>}
        <div className="space-y-3">
          {t === "einvoice" && (<>
            <p className="text-xs text-signal-amber">Create the actual E-Invoice in Tally first, then upload the document and enter its references here.</p>
            <FileUploadField kind="einvoice" label="E-Invoice document *" dataTestId="wf-einvoice-upload" onUploaded={(p) => set("document_path", p)} />
            <div className="grid grid-cols-2 gap-3">
              <F label="Invoice No" value={f.invoice_no || ""} onChange={(v) => set("invoice_no", v)} testId="wf-invoice-no" required />
              <DateField label="Invoice Date" value={f.invoice_date || ""} onChange={(v) => set("invoice_date", v)} testId="wf-invoice-date" />
              <F label="IRN" value={f.irn || ""} onChange={(v) => set("irn", v)} testId="wf-irn" />
              <F label="Ack No" value={f.ack_no || ""} onChange={(v) => set("ack_no", v)} testId="wf-ack-no" />
              <DateField label="Ack Date" value={f.ack_date || ""} onChange={(v) => set("ack_date", v)} testId="wf-ack-date" />
              <F label="E-Way Bill No" value={f.eway_bill || ""} onChange={(v) => set("eway_bill", v)} testId="wf-eway" />
            </div>
            <div><Label className="text-xs">Remarks</Label><Textarea value={f.remarks || ""} onChange={(e) => set("remarks", e.target.value)} data-testid="wf-remarks" /></div>
          </>)}
          {t === "vehicle_checkin" && (<>
            <div className="grid grid-cols-2 gap-3">
              <F label="Vehicle Number" value={f.vehicle_number || ""} onChange={(v) => set("vehicle_number", v)} testId="wf-vehicle-no" required />
              <F label="Driver Name" value={f.driver_name || ""} onChange={(v) => set("driver_name", v)} testId="wf-driver-name" required />
              <F label="Driver Mobile" value={f.driver_mobile || ""} onChange={(v) => set("driver_mobile", v)} testId="wf-driver-mobile" />
              <F label="Transporter" value={f.transporter || ""} onChange={(v) => set("transporter", v)} testId="wf-transporter" />
              <F label="Vehicle Type" value={f.vehicle_type || ""} onChange={(v) => set("vehicle_type", v)} testId="wf-vehicle-type" />
              <F label="Loading Bay" value={f.loading_bay || ""} onChange={(v) => set("loading_bay", v)} testId="wf-loading-bay" />
            </div>
            <div><Label className="text-xs">Remarks</Label><Textarea value={f.remarks || ""} onChange={(e) => set("remarks", e.target.value)} data-testid="wf-remarks" /></div>
          </>)}
          {t === "handover" && (<>
            <div className="grid grid-cols-2 gap-3">
              <F label="Receiver Name" value={f.receiver_name || ""} onChange={(v) => set("receiver_name", v)} testId="wf-receiver" required />
              <F label="Receiver Mobile" value={f.receiver_mobile || ""} onChange={(v) => set("receiver_mobile", v)} testId="wf-receiver-mobile" />
              <F label="Quantity Handed Over" type="number" value={f.quantity || ""} onChange={(v) => set("quantity", v)} testId="wf-handover-qty" required />
            </div>
            <FileUploadField kind="pickup_proof" label="Pickup proof / signature (photo/PDF)" dataTestId="wf-proof-upload" onUploaded={(p) => set("document_path", p)} />
            <div><Label className="text-xs">Remarks</Label><Textarea value={f.remarks || ""} onChange={(e) => set("remarks", e.target.value)} data-testid="wf-remarks" /></div>
          </>)}
          {t === "pod" && (<>
            <FileUploadField kind="pod" label="Signed POD document *" dataTestId="wf-pod-upload" onUploaded={(p) => set("document_path", p)} />
            <div><Label className="text-xs">Remarks</Label><Textarea value={f.remarks || ""} onChange={(e) => set("remarks", e.target.value)} data-testid="wf-remarks" /></div>
          </>)}
          {t === "customer_feedback" && (<>
            <div>
              <Label className="text-xs">Is everything OK with the order? *</Label>
              <Select value={f.option || ""} onValueChange={(v) => set("option", v)}>
                <SelectTrigger data-testid="wf-feedback-option"><SelectValue placeholder="Select outcome" /></SelectTrigger>
                <SelectContent>
                  {[["everything_ok", "Everything OK"], ["qc_concern", "QC Concern"], ["product_mismatch", "Product Mismatch"], ["short_shipment", "Short Shipment"], ["damage", "Damage / Transit Issue"], ["other", "Other Concern"]].map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Remarks {f.option && f.option !== "everything_ok" ? "*" : ""}</Label><Textarea value={f.remarks || ""} onChange={(e) => set("remarks", e.target.value)} data-testid="wf-remarks" /></div>
            <FileUploadField kind="feedback" label="Attachment (optional)" dataTestId="wf-feedback-upload" onUploaded={(p) => set("document_path", p)} />
          </>)}
          {t === "customer_communication" && (<>
            <div>
              <Label className="text-xs">Shared via *</Label>
              <Select value={f.method || ""} onValueChange={(v) => set("method", v)}>
                <SelectTrigger data-testid="wf-comm-method"><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent>
                  {[["email", "Email"], ["whatsapp", "WhatsApp"], ["phone", "Phone"], ["in_person", "In Person"], ["other", "Other"]].map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Remarks</Label><Textarea value={f.remarks || ""} onChange={(e) => set("remarks", e.target.value)} data-testid="wf-remarks" /></div>
          </>)}
          <Button className="w-full h-11 bg-signal-green hover:bg-signal-green/90 text-white font-bold" disabled={busy} onClick={submit} data-testid="wf-complete-submit">
            CONFIRM COMPLETE
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const WORKFLOW_TASK_TYPES = ["einvoice", "vehicle_checkin", "handover", "pod", "customer_feedback", "customer_communication"];
